<template>
    <div class="load-chart">
        <div class="search-box">
            <v-input>
                <i class="fa fa-search"></i>
                <input v-model="searchText" type="text" placeholder="Search" />
            </v-input>
        </div>
        <VueCustomScrollbar class="search-content"
                            @ps-scroll-x="popover.selectedIndex = null"
                            @ps-scroll-y="popover.selectedIndex = null">
            <div class="search-scroll-content">
                <table class="load-chart-items">
                    <thead>
                        <tr>
                            <th>Chart Name</th>
                            <th>Date Modified</th>
                            <th>Applied Filters</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(chart, index) in filteredCharts" :key="chart.id">
                            <td class="item-name">{{ chart.name }}</td>
                            <td class="item-date">{{ formatDate(chart.updated_at) }}</td>
                            <td class="item-tags filter-tags">
                                <div class="filter-tags-inner">
                                    <span class="filter-tag"
                                        v-for="filter in allAppliedFilters(chart)"
                                        :key="`${filter.text}${filter.value}`"
                                    >
                                        {{ filter.text }}
                                    </span>
                                </div>
                            </td>
                            <td class="item-actions">
                                <v-btn class="load-button" @click="loadChart($event, chart.id)">Load</v-btn>
                                <a href="#" class="item-options"
                                   :ref="'element'+chart.id"
                                   @click="togglePopoverMenu($event, chart.id)">
                                    <i class="fa fa-ellipsis-h"></i>
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </VueCustomScrollbar>

         <div class="load-chart-no-results" v-show="!charts || charts.length===0">
            <div class="load-chart-no-results-inner">
                <img src="../../assets/images/no-results.svg" alt="No Results">
                <h4>No results found.</h4>
            </div>
        </div>
        <div class="charts-tab-options">
            <div class="tab-options-menu"
                 v-for="(chart, index) in filteredCharts"
                 :key="index"
                 :class="{ open: popover.selectedIndex === chart.id }"
                 :style="{ top: `${popover.top}px` }" >
                <a href="#" @click.prevent="openPopup(chart, 'rename')">Rename</a>
                <a href="#" @click.prevent="openPopup(chart, 'delete')">Delete</a>
            </div>
        </div>
        <Popup :class-name="showPopup ? 'open' : ''"
               @closePopup="closeRenamePopup">
            <h4 class="popup-title">Rename Chart</h4>
            <div class="popup-content">
                <v-form @submit.prevent="renameChart"
                        ref="renameForm">
                    <v-text-field
                        v-model="renameForm.name"
                        label="Chart Name"
                        :rules="[renameForm.rules.required, renameForm.rules.max]"
                        data-vv-name="Chart Name"
                        data-vv-as="renameChart"
                    />
                    <div class="buttons">
                        <v-btn type="submit" class="popup-btn">Rename</v-btn>
                    </div>
                </v-form>
            </div>
        </Popup>
        <Popup :class-name="showPopupOnDelete ? 'open' : ''"
               @closePopup="() => {showPopupOnDelete = !showPopupOnDelete; popover.selectedIndex = null}">
            <h4 class="popup-title">Delete Chart</h4>
            <div class="popup-content">
                <p class="info-text">
                    Are you sure you want to delete this chart?
                </p>
                <div class="buttons buttons-inline">
                    <v-btn type="button" class="popup-btn" @click="deleteChart">Yes</v-btn>
                    <v-btn type="button" class="popup-btn" @click.stop="()=>{showPopupOnDelete = !showPopupOnDelete; popover.selectedIndex = null}">No</v-btn>
                </div>
            </div>
        </Popup>
    </div>
</template>

<script>
import moment from 'moment';
import VueCustomScrollbar from 'vue-custom-scrollbar';
import Popup from "../common/Popup";

export default {
    components: {
        VueCustomScrollbar,
        Popup,
    },
    props: {
        chartTypes: {
            type: Array,
        },
        cumulativeTypes: {
            type: Array,
        },
        growthRates: {
            type: Array,
        },
        growthRatesQuarters: {
            type: Array,
        },
        companies: {
            type: Array,
        },
        categories: {
            type: Array,
        },
        subcategories: {
            type: Array,
        },
        classes: {
            type: Array,
        },
        ranges: {
            type: Array,
        },
        revenueFilterRanges: {
            type: Array,
        },
        revenues: {
            type: Array,
        },
        excludeBrands: {
            type: Array,
        },
        quarterData: {
            type: Array,
        },
        brands: {
            type: Array,
        },
        tags: {
            type: Array,
        },
    },
    data() {
        return {
            searchText: '',
            charts: [],
            showPopup: false,
            showPopupOnDelete: false,
            deleteChartID: null,
            renameForm: {
                name: '',
                id: '',
                rules: {
                    required: value => !!value || 'Chart Name is required.',
                    max: v => (v && v.length <= 100) || 'Max 100 characters.'
                }
            },
            popover: {
                selectedIndex: null,
                top: 0,
            }
        };
    },
    computed: {
        filteredCharts() {
            if (!this.searchText) {
                return this.charts;
            }

            return this.charts.filter(chart => chart.name.toLowerCase().indexOf(this.searchText.toLowerCase()) >= 0);
        },
    },
    async mounted() {
        window.addEventListener('click', (e) => this.onOutsideClick(e));
        await this.loadCharts();
           this.$root.$on('updateCharts', () => {
               this.loadCharts();
            });
    },
    methods: {
        onOutsideClick(e){
           if (!e.target.classList.contains('fa-ellipsis-h') && e.target.innerHTML !== 'Rename' &&
                        e.target.innerHTML !== 'Delete'){
                    this.popover.selectedIndex = null;
            }
        },
        togglePopoverMenu(event, chartIndex) {
            event.preventDefault();
            if (this.popover.selectedIndex === chartIndex) {
                this.popover.selectedIndex = null;
                return;
            }
            const element = this.$refs['element'+chartIndex][0].getBoundingClientRect();
            this.popover.selectedIndex = chartIndex;
            this.popover.top = element.top + window.scrollY - element.height;
        },
        openPopup(chart, type){
            this.$refs.renameForm.resetValidation();

            this.popover.selectedIndex = null;

            if (type === 'rename'){
                this.showPopup = true;
                this.renameForm.name = chart.name;
                this.renameForm.id = chart.id;
            }else if (type === 'delete'){
                this.showPopupOnDelete = true;
                this.deleteChartID = chart.id;
            }
        },
        closeRenamePopup(){
            this.showPopup = ! this.showPopup;
            this.popover.selectedIndex = null;
        },
        async deleteChart() {
            const chartID = this.deleteChartID;
            await this.$store.dispatch('chart/deleteChart', chartID);
            this.popover.selectedIndex = null;
            this.showPopupOnDelete = false;

            const charts = [...this.charts];
            const chartIndex = charts.findIndex(chart => chart.id === chartID);
            if (chartIndex !== -1) {
                this.charts.splice(chartIndex, 1);
            }
        },
        async renameChart() {
            const validName = await this.$refs.renameForm.validate();

            if (validName) {

                const charts = [...this.charts];
                const chartIndex = charts.findIndex(chart => chart.id === this.renameForm.id);

                const renamedChart = await this.$store.dispatch('chart/renameChart', {chart: this.renameForm, setName: false});

                if(renamedChart) {

                    this.charts[chartIndex].name = this.renameForm.name;

                    this.showPopup = false;
                }
            }

        },
        formatDate(date) {
            return moment(date).format('MMM DD, YYYY [at] hh:mm A');
        },
        async loadCharts() {
            this.charts = await this.$store.dispatch('chart/loadCharts');
        },
        async loadChart(event, id) {
            event.preventDefault();
            await this.$store.dispatch('chart/loadChart', id);
        },
        allAppliedFilters(chart) {
            const chartType = this.chartTypes.find(type => type.value === chart.filters.chartType);
            const cumulativeType = (chart.filters.cumulativeType) ? {
                'text': (chart.filters.cumulativeType === 'none') ? 'Cumulative: Brand' : 'Cumulative: ' + chart.filters.cumulativeType,
                'value': chart.filters.cumulativeType
            } : {'text': 'Cumulative: Brand', 'value': 'none'};
            const chartRange = (chart.filters.range)? {'text':'Launch: '+chart.filters.range, 'value':chart.filters.range} :{'text':'Launch: ALL', 'value':'ALL'};
            const chartRevenueFilterRange =(chart.filters.revenueFilterRange)? {'text':'Revenue: ' +chart.filters.revenueFilterRange, 'value':chart.filters.revenueFilterRange} :{'text':'Revenue: ALL', 'value':'ALL'};
            const chartRevenuesRange =(chart.filters.revenues)? {'text':'Dollars Range: ' +chart.filters.revenues +' Mil', 'value':chart.filters.revenues} :{'text':'Dollars Range: ', 'value':'ALL'};

            const growthRates = chart.filters.growthRates.map(
                growthRateValue => this.growthRates.find(growthRate => growthRate.value === growthRateValue),
            ).filter(function( element ) {
                return element !== undefined;
            });

            const companies = chart.filters.companies.map(
                companyValue => this.companies.find(company => company.value === companyValue),
            ).filter(function( element ) {
                return element !== undefined;
            });

            const categories = chart.filters.categories.map(
                categoryValue => this.categories.find(category => category.value === categoryValue),
            ).filter(function( element ) {
                return element !== undefined;
            });

            const subcategories = chart.filters.subcategories.map(
                subcategoryValue => this.subcategories.find(subcategory => subcategory.value === subcategoryValue),
            ).filter(function( element ) {
                return element !== undefined;
            });

            const classes = chart.filters.classes.map(
                classValue => this.classes.find(classObject => classObject.value === classValue),
            ).filter(function( element ) {
                return element !== undefined;
            });

            const brands = chart.filters.brands.map(
                brandValue => this.brands.find(brand => brand.value === brandValue),
            ).filter(function( element ) {
                return element !== undefined;
            });

            const excludeBrands = chart.filters.excludeBrands.map(
                excludeBrandValue => this.excludeBrands.find(excludeBrand => excludeBrand.value === excludeBrandValue),
            ).filter(function( element ) {
                return element !== undefined;
            });

            const excludeBrandArray = [];

            excludeBrands.forEach((entry) => {
                excludeBrandArray.push(entry.text);
            });

            const excludeBrandString = {'text': 'Exclude Brands: ' + (excludeBrandArray.length > 0 ? excludeBrandArray.join(', ') : '-'), 'value': 'ALL'};

            const chartCurves = (chart.filters.curves) ? { 'text' : 'Top Curves: ' + chart.filters.curves, 'value' : chart.filters.curves} :{ 'text' : 'Top Curves: -', 'value' : 'ALL'};

            const tags = chart.filters.tags.map(
                tagValue => this.tags.find(tag => tag.value === tagValue),
            );

            const quarterData = (chart.filters.quarterData) ? { 'text' : 'Quarters: ' + chart.filters.quarterData, 'value' : chart.filters.quarterData } : { 'text' : 'Quarters: ALL', 'value' : 'ALL' };
            
            let result = [];

            switch (chart.filters.chartType) {
                case 'growth':
                    result = [chartType, ...growthRates, ...companies, ...categories, ...subcategories, ...classes, excludeBrandString, ...brands, ...tags, chartCurves, quarterData];
                    break;
                case 'new':
                    result = [chartType, chartRange, chartRevenueFilterRange, chartRevenuesRange, ...companies, ...categories, ...subcategories, ...classes, excludeBrandString, ...brands, ...tags, chartCurves, quarterData];
                    break;
                default:
                    result = [chartType, cumulativeType, chartRange, chartRevenueFilterRange, ...companies, ...categories, ...subcategories, ...classes, excludeBrandString, ...brands, ...tags, chartCurves, quarterData];
                    break;
            }
            
            if (chart.filters.showCumulativeRevenueChart){
                result=[{ 'text' : 'Cumulative Revenue'}, ...result];
            }

            if (chart.filters.startSyncView){
                result=[{ 'text' : 'Sync View'}, ...result];
            }
            
           return result;
        },
    },
};

</script>

<style lang="scss">
    @import '../../style/Charts/LoadChartComponent.scss';
</style>
